// A rule-based English proofreader for learner sentences.
//
// A direct port of the Android build's `tutor/GrammarChecker.kt`, which itself
// replaced the Claude API call the original React Native version made: every
// rule below runs locally in a few microseconds, so the app needs no key, no
// server and no network connection. Rules are conservative on purpose — when a
// word isn't in `englishWords.ts` the sentence is left alone rather than
// "corrected" into nonsense.
import {
  baseOfPast,
  baseOfThirdPerson,
  gerund,
  isBaseVerb,
  jobNouns,
  needsAn,
  overRegularisedPast,
  past,
  progressiveSafeVerbs,
  stativeVerbs,
  thirdPerson,
  uncountablePlurals,
} from './englishWords';

/** One thing the tutor noticed and rewrote. */
export interface Correction {
  rule: string;
  original: string;
  corrected: string;
  explanation: string;
  /** Lower sorts first: real grammar before spelling/punctuation nitpicks. */
  priority: number;
}

/** Outcome of running every rule over one learner message. */
export interface GrammarResult {
  original: string;
  corrected: string;
  corrections: Correction[];
}

export function isClean(result: GrammarResult): boolean {
  return result.corrections.length === 0;
}

/** A regex match from `matchAll`, where `index` is always present. */
type Match = RegExpMatchArray & { index: number };

interface Rule {
  id: string;
  /** Always carries the `g` flag so `matchAll` can walk it. */
  regex: RegExp;
  explanation: string;
  priority: number;
  transform: (m: Match, text: string) => string | null;
}

/** Kotlin's `groupValues[i]` yields "" for a group that didn't participate. */
function g(m: Match, index: number): string {
  return m[index] ?? '';
}

export function check(input: string): GrammarResult {
  let text = input;
  const found: Correction[] = [];
  for (const rule of rules) {
    text = applyRule(rule, text, found);
  }
  return {
    original: input,
    corrected: text,
    // `sort` is stable in every engine we target, so equal priorities keep the
    // order the rules fired in — same as Kotlin's `sortedBy`.
    corrections: [...found].sort((a, b) => a.priority - b.priority),
  };
}

function applyRule(rule: Rule, text: string, out: Correction[]): string {
  let sb = '';
  let cursor = 0;
  for (const raw of text.matchAll(rule.regex)) {
    const m = raw as Match;
    if (m.index < cursor) continue;
    const replacement = rule.transform(m, text);
    if (replacement === null) continue;
    // Capitalisation is never corrected: this is spoken practice, where an
    // upper- or lower-case letter carries no meaning at all.
    if (replacement.toLowerCase() === m[0].toLowerCase()) continue;
    sb += text.slice(cursor, m.index) + replacement;
    cursor = m.index + m[0].length;
    out.push({
      rule: rule.id,
      original: m[0].trim(),
      corrected: replacement.trim(),
      explanation: rule.explanation,
      priority: rule.priority,
    });
  }
  sb += text.slice(cursor);
  return sb;
}

// ------------------------------------------------------------------- helpers

/** Copies the capitalisation of `source` onto `replacement`. */
function matchCase(source: string, replacement: string): string {
  const first = source.charAt(0);
  const isUpper = first !== '' && first !== first.toLowerCase() && first === first.toUpperCase();
  return isUpper ? replacement.charAt(0).toUpperCase() + replacement.slice(1) : replacement;
}

function beFormFor(subject: string): string {
  switch (subject.toLowerCase()) {
    case 'i':
      return 'am';
    case 'he':
    case 'she':
    case 'it':
      return 'is';
    default:
      return 'are';
  }
}

function pastBeFormFor(subject: string): string {
  return ['i', 'he', 'she', 'it'].includes(subject.toLowerCase()) ? 'was' : 'were';
}

/** True when `index` begins a sentence (start of text, or right after . ! ?). */
function startsSentence(text: string, index: number): boolean {
  for (let i = index - 1; i >= 0; i--) {
    const c = text.charAt(i);
    if (/\s/.test(c)) continue;
    return c === '.' || c === '!' || c === '?';
  }
  return true;
}

/** The word immediately before `index`, lowercased; empty at the start of the text. */
function precedingWord(text: string, index: number): string {
  let end = index;
  while (end > 0 && /\s/.test(text.charAt(end - 1))) end--;
  let start = end;
  while (start > 0 && /\p{L}/u.test(text.charAt(start - 1))) start--;
  return text.slice(start, end).toLowerCase();
}

const AUXILIARIES: ReadonlySet<string> = new Set([
  'am', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had',
  'do', 'does', 'did', 'will', 'would', 'can', 'could', 'shall', 'should',
  'may', 'might', 'must', 'not',
]);

const PAST_TIME_MARKER =
  /\b(yesterday|last\s+(?:night|week|month|year|weekend|summer|winter)|\d+\s+(?:minutes?|hours?|days?|weeks?|months?|years?)\s+ago|ago)\b/i;

/** Verbs safe to attach "to" to after want/need/like — rarely used as nouns. */
const INFINITIVE_SAFE: ReadonlySet<string> = new Set([
  'go', 'eat', 'see', 'buy', 'watch', 'learn', 'study', 'travel', 'visit',
  'meet', 'read', 'write', 'speak', 'sleep', 'swim', 'run', 'walk', 'sing',
  'drive', 'become', 'understand', 'know', 'take', 'come', 'tell', 'ask',
  'find', 'leave', 'stay', 'start', 'finish', 'improve', 'explain', 'listen',
]);

/** Verbs safe to turn into "-ing" after enjoy/finish/practice. */
const GERUND_SAFE: ReadonlySet<string> = new Set([
  'go', 'eat', 'watch', 'read', 'write', 'study', 'travel', 'swim', 'run',
  'walk', 'sing', 'dance', 'cook', 'drive', 'talk', 'speak', 'listen', 'play',
  'learn', 'clean', 'shop', 'exercise', 'meet', 'visit', 'drink', 'sleep',
]);

const PLURAL_COUNTABLE: ReadonlySet<string> = new Set([
  'people', 'books', 'friends', 'things', 'times', 'years', 'students', 'cars',
  'days', 'words', 'questions', 'places', 'movies', 'songs', 'ideas', 'photos',
  'children', 'countries', 'cities', 'hours', 'minutes', 'problems',
]);

const UNCOUNTABLE: ReadonlySet<string> = new Set([
  'money', 'time', 'water', 'information', 'homework', 'food', 'work', 'rice',
  'bread', 'milk', 'coffee', 'tea', 'music', 'advice', 'help', 'traffic',
  'furniture', 'luggage', 'knowledge', 'progress', 'news',
]);

/**
 * Explicit comparative/superlative list. Matching `\w+er` instead would break
 * ordinary nouns — "more water" must not become "water".
 */
const COMPARATIVES: ReadonlySet<string> = new Set([
  'better', 'worse', 'best', 'worst', 'bigger', 'smaller', 'faster', 'slower',
  'older', 'younger', 'taller', 'shorter', 'happier', 'easier', 'harder',
  'nicer', 'cheaper', 'richer', 'stronger', 'warmer', 'colder', 'hotter',
  'longer', 'higher', 'lower', 'greater', 'closer', 'safer', 'cleaner',
  'busier', 'prettier', 'funnier', 'biggest', 'smallest', 'fastest', 'oldest',
  'youngest', 'tallest', 'happiest', 'easiest', 'hardest', 'nicest',
  'cheapest', 'longest', 'highest', 'lowest', 'greatest', 'closest', 'safest',
  'cleanest', 'busiest', 'prettiest', 'funniest', 'strongest', 'warmest',
  'coldest', 'hottest', 'richest',
]);

const WEEKDAYS: readonly string[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];

const MONTHS: readonly string[] = [
  'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
  'september', 'october', 'november', 'december',
];

const CONTRACTIONS: Readonly<Record<string, string>> = {
  dont: "don't", cant: "can't", wont: "won't", didnt: "didn't",
  doesnt: "doesn't", isnt: "isn't", arent: "aren't",
  wasnt: "wasn't", werent: "weren't", couldnt: "couldn't",
  shouldnt: "shouldn't", wouldnt: "wouldn't", havent: "haven't",
  hasnt: "hasn't", hadnt: "hadn't", im: "I'm", ive: "I've",
  youre: "you're", theyre: "they're", thats: "that's",
  whats: "what's", wouldve: "would've", couldve: "could've",
};

/** Fixed phrase swaps: wrong preposition or extra/missing particle. */
const PHRASE_FIXES: ReadonlyArray<readonly [string, string, string]> = [
  ['\\bexplain\\s+(me|him|her|us|them)\\b', 'explain to $1',
    '"Explain" needs "to" before the person: explain to me.'],
  ['\\b(discuss(?:ed|es|ing)?)\\s+about\\b', '$1',
    '"Discuss" already means "talk about" — no "about" after it.'],
  ['\\bmarried\\s+with\\b', 'married to',
    'We are married *to* someone, not *with* them.'],
  ['\\b(depends?|depended|depending)\\s+of\\b', '$1 on',
    'The phrase is "depend on".'],
  ['\\bgood\\s+in\\s+(english|math|sports|cooking|music|school)\\b',
    'good at $1', 'We are "good at" a skill or subject.'],
  ['\\barrive\\s+to\\b', 'arrive at',
    'We "arrive at" a place (or "arrive in" a city).'],
  ['\\blisten\\s+(?!to\\b)(music|songs?|the radio|podcasts?)\\b',
    'listen to $1', '"Listen" always takes "to" before what you hear.'],
  ['\\bgo\\s+to\\s+home\\b', 'go home',
    '"Home" doesn\'t take "to": go home.'],
  ['\\banswer\\s+to\\s+(the\\s+|my\\s+|your\\s+)?question\\b',
    'answer $1question', '"Answer" takes no "to": answer the question.'],
  ['\\bworry\\s+for\\b', 'worry about',
    'We "worry about" something.'],
  ['\\binterested\\s+(about|on|for)\\b', 'interested in',
    'The phrase is "interested in".'],
  ['\\bafraid\\s+(from|about)\\b', 'afraid of',
    'The phrase is "afraid of".'],
  ['\\bsince\\s+(\\d+|a\\s+few|many|several)\\s+(year|month|week|day|hour|minute)s?\\b',
    'for $1 $2s', 'Use "for" with a length of time, "since" with a start point.'],
  ['\\bduring\\s+(\\d+)\\s+(year|month|week|day|hour|minute)s?\\b',
    'for $1 $2s', 'Use "for" with a length of time.'],
  ['\\bnowadays\\s+days\\b', 'nowadays', '"Nowadays" is already one word.'],
  ['\\bin\\s+the\\s+night\\b', 'at night', 'The fixed phrase is "at night".'],
  ['\\bmake\\s+(a\\s+)?homework\\b', 'do homework', 'We "do" homework.'],
  ['\\bdo\\s+a\\s+mistake\\b', 'make a mistake', 'We "make" a mistake.'],
  ['\\btake\\s+a\\s+decision\\b', 'make a decision', 'We "make" a decision.'],
];

// --------------------------------------------------------------------- rules

const rules: Rule[] = buildRules();

function buildRules(): Rule[] {
  const list: Rule[] = [];

  list.push({
    id: 'contraction_apostrophe',
    regex: new RegExp(`\\b(${Object.keys(CONTRACTIONS).join('|')})\\b`, 'gi'),
    explanation: 'Contractions need an apostrophe.',
    priority: 7,
    transform: (m) => {
      const fixed = CONTRACTIONS[m[0].toLowerCase()];
      return fixed === undefined ? null : matchCase(m[0], fixed);
    },
  });

  list.push({
    id: 'irregular_past',
    regex: new RegExp(`\\b(${Object.keys(overRegularisedPast).join('|')})\\b`, 'gi'),
    explanation: 'This verb has an irregular past form — it doesn\'t take "-ed".',
    priority: 1,
    transform: (m) => {
      const fixed = overRegularisedPast[m[0].toLowerCase()];
      return fixed === undefined ? null : matchCase(m[0], fixed);
    },
  });

  list.push({
    id: 'age_with_be',
    regex: /\b(I|you|he|she|we|they)\s+(have|has|had)\s+(\d{1,3})\s+years?(\s+old)?\b/gi,
    explanation: 'In English you *are* an age, you don\'t *have* it: I am 25 years old.',
    priority: 1,
    transform: (m) => {
      const subject = g(m, 1);
      const be = g(m, 2).toLowerCase() === 'had' ? pastBeFormFor(subject) : beFormFor(subject);
      return `${subject} ${be} ${g(m, 3)} years old`;
    },
  });

  list.push({
    id: 'be_agreement',
    regex: /\b(I|you|he|she|it|we|they|people)\s+(am|is|are|was|were)\b/gi,
    explanation: 'The verb "be" has to match its subject.',
    priority: 1,
    transform: (m, text) => {
      // "If I were you…" is the subjunctive, not a mistake.
      if (precedingWord(text, m.index) === 'if') return null;
      const subject = g(m, 1);
      const be = g(m, 2).toLowerCase();
      const isPast = be === 'was' || be === 'were';
      const expected = isPast ? pastBeFormFor(subject) : beFormFor(subject);
      return be === expected ? null : `${subject} ${expected}`;
    },
  });

  list.push({
    id: 'stative_no_be',
    regex: new RegExp(
      `\\b(I|you|he|she|it|we|they)\\s+(am|is|are)\\s+(${stativeVerbs.join('|')})\\b`,
      'gi',
    ),
    explanation: 'This verb doesn\'t use "be": say "I agree", not "I am agree".',
    priority: 1,
    transform: (m) => {
      const subject = g(m, 1);
      const verb = g(m, 3).toLowerCase();
      const conjugated = ['he', 'she', 'it'].includes(subject.toLowerCase())
        ? thirdPerson(verb)
        : verb;
      return `${subject} ${conjugated}`;
    },
  });

  list.push({
    id: 'progressive_ing',
    regex: /\b(am|is|are|was|were)\s+([a-z]+)\b/gi,
    explanation: 'After am/is/are the verb takes "-ing".',
    priority: 2,
    transform: (m) => {
      const verb = g(m, 2).toLowerCase();
      if (!progressiveSafeVerbs.has(verb)) return null;
      return `${g(m, 1)} ${gerund(verb)}`;
    },
  });

  list.push({
    id: 'aux_base_form',
    regex: /\b(do|does|did|don't|doesn't|didn't)\s+([a-z]+)\b/gi,
    explanation: 'After do/does/did the verb stays in its base form.',
    priority: 1,
    transform: (m) => {
      const word = g(m, 2).toLowerCase();
      if (AUXILIARIES.has(word)) return null;
      const base = baseOfPast(word) ?? baseOfThirdPerson(word);
      if (base === null || base === word) return null;
      return `${g(m, 1)} ${base}`;
    },
  });

  list.push({
    id: 'modal_base_form',
    regex: /\b(can|could|will|would|shall|should|may|might|must)\s+([a-z]+)\b/gi,
    explanation: 'After a modal (can, will, should…) the verb stays in its base form.',
    priority: 1,
    transform: (m) => {
      const word = g(m, 2).toLowerCase();
      if (AUXILIARIES.has(word) || word === 'not') return null;
      const base = baseOfPast(word) ?? baseOfThirdPerson(word);
      if (base === null || base === word) return null;
      return `${g(m, 1)} ${base}`;
    },
  });

  list.push({
    id: 'subject_dont',
    regex: /\b(he|she|it)\s+(don't|do\s+not)\b/gi,
    explanation: 'He/she/it takes "doesn\'t".',
    priority: 1,
    transform: (m) => `${g(m, 1)} doesn't`,
  });

  list.push({
    id: 'subject_doesnt',
    regex: /\b(I|you|we|they|people)\s+(doesn't|does\s+not)\b/gi,
    explanation: 'I/you/we/they take "don\'t".',
    priority: 1,
    transform: (m) => `${g(m, 1)} don't`,
  });

  list.push({
    id: 'third_person_s',
    regex: /\b(he|she|it)\s+([a-z]+)\b/gi,
    explanation: 'After he/she/it the present-tense verb takes "-s".',
    priority: 1,
    transform: (m) => {
      const verb = g(m, 2).toLowerCase();
      // "he have" / "he do" are genuine mistakes, so those three base forms are
      // checked even though they are otherwise treated as auxiliaries.
      if (AUXILIARIES.has(verb) && !['have', 'do', 'be'].includes(verb)) return null;
      if (!isBaseVerb(verb)) return null;
      return `${g(m, 1)} ${thirdPerson(verb)}`;
    },
  });

  list.push({
    id: 'plural_no_s',
    regex: /\b(I|you|we|they|people)\s+([a-z]+s)\b/gi,
    explanation: 'Only he/she/it adds "-s" — I/you/we/they use the base verb.',
    priority: 1,
    transform: (m) => {
      const word = g(m, 2).toLowerCase();
      if (AUXILIARIES.has(word)) return null;
      const base = baseOfThirdPerson(word);
      if (base === null || base === 'be' || base === word) return null;
      return `${g(m, 1)} ${base}`;
    },
  });

  list.push({
    id: 'past_tense_needed',
    regex: /\b(I|you|he|she|it|we|they)\s+([a-z]+)\b/gi,
    explanation: 'A past time word (yesterday, last week, ago) needs the past tense.',
    priority: 2,
    transform: (m, text) => {
      if (!PAST_TIME_MARKER.test(text)) return null;
      const word = g(m, 2).toLowerCase();
      if (AUXILIARIES.has(word)) return null;
      const base = isBaseVerb(word) ? word : baseOfThirdPerson(word);
      if (base === null) return null;
      const pastForm = past(base);
      if (pastForm === word) return null;
      return `${g(m, 1)} ${pastForm}`;
    },
  });

  list.push({
    id: 'infinitive_to',
    regex:
      /\b(want|wants|wanted|need|needs|needed|like|likes|liked|hope|hopes|plan|plans|planned|decide|decides|decided|try|tries|tried|learn|learns|learned)\s+([a-z]+)\b/gi,
    explanation: 'These verbs are followed by "to" + verb: I want to go.',
    priority: 2,
    transform: (m) => {
      const next = g(m, 2).toLowerCase();
      if (!INFINITIVE_SAFE.has(next)) return null;
      return `${g(m, 1)} to ${next}`;
    },
  });

  list.push({
    id: 'gerund_after_verb',
    regex:
      /\b(enjoy|enjoys|enjoyed|finish|finishes|finished|practice|practices|practiced|avoid|avoids|avoided|mind|minds|suggest|suggests|keep|keeps)\s+(to\s+)?([a-z]+)\b/gi,
    explanation: 'These verbs are followed by "-ing": I enjoy swimming.',
    priority: 2,
    transform: (m) => {
      const next = g(m, 3).toLowerCase();
      if (!GERUND_SAFE.has(next)) return null;
      return `${g(m, 1)} ${gerund(next)}`;
    },
  });

  list.push({
    id: 'double_comparative',
    regex: /\b(more|most)\s+([a-z]+)\b/gi,
    explanation: 'Use either "more" or the "-er/-est" ending, never both.',
    priority: 3,
    transform: (m) => {
      if (!COMPARATIVES.has(g(m, 2).toLowerCase())) return null;
      return matchCase(m[0], g(m, 2));
    },
  });

  list.push({
    id: 'double_negative',
    regex:
      /\b(don't|doesn't|didn't|can't|won't|isn't|aren't|haven't|hasn't|never)((?:\s+\w+){0,2}\s+)(no|nothing|nobody|nowhere)\b/gi,
    explanation: 'English uses one negative per clause: "I don\'t have any…".',
    priority: 2,
    transform: (m) => {
      const word = g(m, 3).toLowerCase();
      const positive =
        word === 'no' ? 'any' : word === 'nothing' ? 'anything' : word === 'nobody' ? 'anybody' : 'anywhere';
      return `${g(m, 1)}${g(m, 2)}${positive}`;
    },
  });

  list.push({
    id: 'article_a_an',
    regex: /\b(a|an)\s+([A-Za-z]+)\b/g,
    explanation: 'Use "an" before a vowel *sound*, "a" before a consonant sound.',
    priority: 3,
    transform: (m) => {
      const article = g(m, 1);
      const word = g(m, 2);
      const correct = needsAn(word) ? 'an' : 'a';
      if (article.toLowerCase() === correct) return null;
      return `${matchCase(article, correct)} ${word}`;
    },
  });

  list.push({
    id: 'missing_article_job',
    regex: new RegExp(`\\b(am|is|are|was|were)\\s+(${jobNouns.join('|')})\\b`, 'gi'),
    explanation: 'Singular jobs need an article: "I am a student".',
    priority: 2,
    transform: (m) => {
      const noun = g(m, 2);
      const article = needsAn(noun) ? 'an' : 'a';
      return `${g(m, 1)} ${article} ${noun}`;
    },
  });

  list.push({
    id: 'uncountable_plural',
    regex: new RegExp(`\\b(${Object.keys(uncountablePlurals).join('|')})\\b`, 'gi'),
    explanation: 'This noun is uncountable in English — it has no plural "-s".',
    priority: 3,
    transform: (m) => {
      const fixed = uncountablePlurals[m[0].toLowerCase()];
      return fixed === undefined ? null : matchCase(m[0], fixed);
    },
  });

  list.push({
    id: 'much_many',
    regex: /\b(much|many)\s+([a-z]+)\b/gi,
    explanation: '"Many" goes with countable nouns, "much" with uncountable ones.',
    priority: 3,
    transform: (m) => {
      const quantifier = g(m, 1).toLowerCase();
      const noun = g(m, 2).toLowerCase();
      if (quantifier === 'much' && PLURAL_COUNTABLE.has(noun)) {
        return `${matchCase(g(m, 1), 'many')} ${g(m, 2)}`;
      }
      if (quantifier === 'many' && UNCOUNTABLE.has(noun)) {
        return `${matchCase(g(m, 1), 'much')} ${g(m, 2)}`;
      }
      return null;
    },
  });

  list.push({
    id: 'there_are',
    regex:
      /\bthere\s+is\s+((?:many|a\s+lot\s+of|lots\s+of|some|several|two|three|four|five|\d+)\s+[a-z]+)/gi,
    explanation: 'Plural subjects take "there are".',
    priority: 2,
    transform: (m) => `${matchCase(m[0], 'there')} are ${g(m, 1)}`,
  });

  list.push({
    id: 'day_preposition',
    regex: new RegExp(`\\bin\\s+(${WEEKDAYS.join('|')}|the\\s+weekend|my\\s+birthday)\\b`, 'gi'),
    explanation: 'Days take "on": on Monday, on the weekend.',
    priority: 3,
    transform: (m) => `${matchCase(m[0], 'on')} ${g(m, 1)}`,
  });

  list.push({
    id: 'month_preposition',
    regex: new RegExp(`\\bon\\s+(${MONTHS.join('|')}|\\d{4})\\b`, 'gi'),
    explanation: 'Months and years take "in": in July, in 2026.',
    priority: 3,
    transform: (m) => `${matchCase(m[0], 'in')} ${g(m, 1)}`,
  });

  list.push({
    id: 'adverb_well',
    // "good" must end the clause, so "I did good work" stays untouched.
    regex:
      /\b(speak|speaks|spoke|write|writes|wrote|sing|sings|sang|dance|dances|cook|cooks|drive|drives|sleep|sleeps|slept|study|studies|studied|work|works|worked|play|plays|played|do|does|did)\s+((?:[a-z]+\s+){0,2})good\b(?=\s*[.,!?]|\s*$)/gi,
    explanation: 'Describe *how* you do something with "well", not "good".',
    priority: 2,
    transform: (m) => `${g(m, 1)} ${g(m, 2)}well`,
  });

  list.push({
    id: 'question_word_order',
    regex:
      /\b(how|what|where|when|why|who)\s+(I|you|he|she|it|we|they)\s+(can|could|should|will|would|do|does|did|am|is|are|was|were)\b/gi,
    explanation: 'In a direct question the helping verb comes before the subject.',
    priority: 2,
    transform: (m, text) => {
      if (!startsSentence(text, m.index)) return null;
      return `${g(m, 1)} ${g(m, 3)} ${g(m, 2)}`;
    },
  });

  list.push({
    id: 'every_day',
    regex: /\beveryday\b(?!\s+(life|routine|english|words?|use|activities|items|things|clothes))/gi,
    explanation: '"Every day" (two words) means daily; "everyday" is an adjective.',
    priority: 4,
    transform: (m) => matchCase(m[0], 'every day'),
  });

  for (const [pattern, replacement, explanation] of PHRASE_FIXES) {
    list.push({
      id: 'phrase_' + (pattern.match(/[A-Za-z]/g) ?? []).join('').slice(0, 12).toLowerCase(),
      regex: new RegExp(pattern, 'gi'),
      explanation,
      priority: 2,
      transform: (m) => {
        let out = replacement;
        for (let i = m.length - 1; i >= 0; i--) {
          out = out.split(`$${i}`).join(g(m, i));
        }
        return matchCase(m[0], out);
      },
    });
  }

  return list;
}
